/**
 * 서버 오류 → 사람이 읽을 한 줄 (v2.228.1 · 2026-10-06 시나리오 점검)
 *
 * 왜 있나
 *   서버는 입력 검증(Zod)에 걸리면 `{ code:'VALIDATION_ERROR', message:'입력값 검증에 실패했습니다', details:{…칸별 이유…} }` 를
 *   준다. 영업건·사내결재 창은 `message` 만 띄워 **어느 칸이 왜** 틀렸는지 안 보였다(점검 실측: 납품 시간 순서가 틀린 건을
 *   고치려 해도 「입력값 검증에 실패했습니다」만 떠서 원인을 못 찾았다 · 비고 201자도 같았다).
 *   → details 에서 **첫 번째 칸의 이유**를 꺼내 「칸 이름: 이유」로 붙인다. 영어 기본 문구는 흔한 것만 한국어로 바꾼다.
 */

/** 서버 칸 이름 → 화면 이름 (영업건·사내결재에서 흔한 것 · 없으면 칸 이름 그대로) */
const LABELS: Record<string, string> = {
  // 영업건
  empId: '영업담당자', companyId: '사업자', projectName: '프로젝트명', endUser: '엔드유저',
  invoiceCompany: '계산서발행업체', proposalCompany: '제안업체', deliveryPlace: '납품장소',
  contactPerson: '거래처담당자', itemSummary: '제안품목', proposalPrice: '제안가',
  contractDate: '계약일', deliveryDate: '납품완료일', expectedDeliveryDate: '납품예정일',
  deliveryStartTime: '납품 시작시간', deliveryEndTime: '납품 종료시간', invoiceDate: '계산서발행일',
  note: '비고', costNote: '비용참고', parentSalesId: '원래 매출번호',
  // 사내결재
  docDate: '작성일', purpose: '목적', vendor: '구매처', payMethod: '결제방법', payDateText: '결제날짜',
  bankHolder: '예금주', bankName: '은행', bankAccount: '계좌번호', periodFrom: '사용기간(시작)',
  periodTo: '사용기간(끝)', items: '내역', itemDate: '일자', account: '계정과목', subAccount: '보조과목',
  usageTarget: '사용처', description: '품목', qty: '수량', unitPrice: '단가', amount: '금액',
};

/** Zod 기본 영어 문구 → 한국어 (흔한 것만 · 나머지는 그대로) */
function koMessage(m: string): string {
  let r: RegExpExecArray | null;
  if (m === 'Required') return '꼭 입력해야 합니다';
  if ((r = /^String must contain at most (\d+) character\(s\)$/.exec(m))) return `${r[1]}자까지 입력할 수 있습니다`;
  if ((r = /^String must contain at least (\d+) character\(s\)$/.exec(m))) {
    return r[1] === '1' ? '꼭 입력해야 합니다' : `${r[1]}자 이상 입력하세요`;
  }
  if ((r = /^Number must be less than or equal to (\S+)$/.exec(m))) return `${Number(r[1]).toLocaleString('ko-KR')} 이하로 입력하세요`;
  if ((r = /^Number must be greater than or equal to (\S+)$/.exec(m))) return `${Number(r[1]).toLocaleString('ko-KR')} 이상으로 입력하세요`;
  if ((r = /^Number must be greater than (\S+)$/.exec(m))) return `${Number(r[1]).toLocaleString('ko-KR')}보다 크게 입력하세요`;
  if (/^Expected number, received/.test(m)) return '숫자를 입력하세요';
  if (/^Expected string, received/.test(m)) return '글자로 입력하세요';
  if ((r = /^Array must contain at most (\d+) element\(s\)$/.exec(m))) return `${r[1]}개까지만 넣을 수 있습니다`;
  if ((r = /^Array must contain at least (\d+) element\(s\)$/.exec(m))) return `${r[1]}개 이상 넣어야 합니다`;
  if (/^Invalid enum value/.test(m)) return '고를 수 있는 값이 아닙니다';
  if (m === 'Invalid email') return '메일 주소 모양이 아닙니다';
  return m;
}

/** z.format() 모양에서 첫 번째 이유와 그 칸의 경로를 찾는다 */
function firstIssue(node: unknown, path: string[] = []): { path: string[]; message: string } | null {
  if (!node || typeof node !== 'object') return null;
  const o = node as Record<string, unknown>;
  const errs = o._errors;
  if (Array.isArray(errs) && errs.length > 0 && typeof errs[0] === 'string' && path.length > 0) {
    return { path, message: errs[0] };
  }
  for (const [k, v] of Object.entries(o)) {
    if (k === '_errors') continue;
    const hit = firstIssue(v, [...path, k]);
    if (hit) return hit;
  }
  // 칸이 아닌 문서 전체 이유(refine)
  if (Array.isArray(errs) && errs.length > 0 && typeof errs[0] === 'string') return { path, message: errs[0] };
  return null;
}

/** 경로 → 사람 말: ['items','2','amount'] → 「내역 3번째 줄 · 금액」 */
function pathLabel(path: string[]): string {
  const parts: string[] = [];
  for (const p of path) {
    if (/^\d+$/.test(p)) {
      if (parts.length > 0) parts[parts.length - 1] = `${parts[parts.length - 1]} ${Number(p) + 1}번째 줄`;
      else parts.push(`${Number(p) + 1}번째`);
    } else {
      parts.push(LABELS[p] ?? p);
    }
  }
  return parts.join(' · ');
}

/**
 * 서버 오류 응답(error 부분) → 화면에 띄울 한 줄.
 * VALIDATION_ERROR 에 칸별 이유(details)가 있으면 「입력값을 확인하세요 — 칸: 이유」, 아니면 서버 문구 그대로.
 */
export function apiErrorText(error: { code?: string; message?: string; details?: unknown } | null | undefined): string {
  const base = error?.message || '요청을 처리하지 못했습니다';
  if (error?.code !== 'VALIDATION_ERROR' || !error.details) return base;
  const hit = firstIssue(error.details);
  if (!hit) return base;
  const where = hit.path.length > 0 ? `${pathLabel(hit.path)}: ` : '';
  return `입력값을 확인하세요 — ${where}${koMessage(hit.message)}`;
}
